import { useCallback, useEffect, useState } from "react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { DotLottiePlayer } from "@dotlottie/react-player";
import classNames from "classnames";
import { KamaModuleContext } from "@kamatech-ir/kama-react-module";
import { useAppDispatch, useAppSelector } from "hooks/redux.hook";
import http from "utils/http";
import tools from "utils/tools";
import {
  clearToken,
  reloadToken,
  setIsLoginAnimationVisible,
  syncToken,
} from "redux/slices/app.slice";
import AuthorizedLayout from "layout/authorized/AuthorizedLayout";
import AuthPage from "./pages/auth/AuthPage";
import ProfilePage from "pages/profile/ProfilePage";
import SuccessLoginJSON from "assets/images/success-login.json";
import styles from "./App.module.scss";
import HomePage from "pages/home/HomePage";

const broadcastChannel = new BroadcastChannel("tokenChannel");

const authorizedRouter = createBrowserRouter([
  {
    path: "/",
    element: <AuthorizedLayout />,
    children: [
      {
        path: "/",
        element: <HomePage />,
      },
      {
        path: "/profile",
        element: <ProfilePage />,
      },
    ],
  },
]);
const unauthorizedRouter = createBrowserRouter([
  {
    path: "*",
    element: <AuthPage />,
  },
]);

const isIframe = window.self !== window.top;
function App() {
  const dispatch = useAppDispatch();
  const {
    token,
    clientId,
    redirectUrl,
    callback,
    isLoginAnimationVisible,
    initialQueryString,
  } = useAppSelector((state) => {
    return state.app;
  });
  const [statusLoading, setStatusLoading] = useState<boolean>(true);
  const [isRemovingAnimation, setIsRemovingAnimation] =
    useState<boolean>(false);

  // refactor, merge fetch sso key functions
  const fetchSsoKeyCallback = useCallback(async () => {
    try {
      const response = await http.post({
        url: "/api/Key/Generate",
        data: { clientId: clientId },
      });

      const queryStringObj = new URLSearchParams(initialQueryString);
      queryStringObj.delete("clientid");
      queryStringObj.delete("callback");
      queryStringObj.append(
        "ssokey",
        response.code === 403 ? -1 : response.data.ssoKey
      );

      window.location.href = `${
        response.data.callBack
      }?${queryStringObj.toString()}`;
    } catch (error) {}
  }, [clientId, initialQueryString]);
  const fetchSsoKey = useCallback(async () => {
    try {
      const response = await http.post({
        url: "/api/Key/Generate",
        data: { clientId: clientId },
      });

      const domain = (redirectUrl || "").match(/^(https?:\/\/[^/]+)/); // refactor, get this from parent of iframe

      if (domain && domain.length) {
        window.parent.postMessage(
          {
            ssoKey: response.code === 403 ? -1 : response.data.ssoKey,
            type: "sso",
          },
          domain[0]
        );
      }
    } catch (error) {}
  }, [clientId, redirectUrl]);

  useEffect(() => {
    if (isIframe && token && clientId) {
      fetchSsoKey();
    }
  }, [token, clientId, fetchSsoKey]);

  useEffect(() => {
    if (token && clientId && callback) {
      fetchSsoKeyCallback();
    }
  }, [token, clientId, callback, fetchSsoKeyCallback]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.has("logout")) {
      tools.removeQueryParam("logout");
      dispatch(clearToken());
    }
    if (queryParams.has("warning")) {
      tools.alert.warning(queryParams.get("warning")?.toString());
      tools.removeQueryParam("warning");
    }
  }, [dispatch]);

  useEffect(() => {
    const checkStatus = async () => {
      if (token) {
        if (!document.hidden) {
          try {
            await http.post({ url: "/api/Authentication/Status" });
          } finally {
            setStatusLoading(false);
          }
        }
      } else {
        dispatch(reloadToken());
      }

      setStatusLoading(false);
    };

    checkStatus();

    document.addEventListener("visibilitychange", checkStatus, false);

    return () => {
      document.removeEventListener("visibilitychange", checkStatus, false);
    };
  }, [dispatch, token]);

  // listen to token channel
  useEffect(() => {
    broadcastChannel.onmessage = function (event) {
      if (event.data.type === "tokenUpdate") {
        if (
          event.data &&
          event.data.data &&
          event.data.data.access_token !== token
        ) {
          dispatch(syncToken(event.data.data));
        }
      } else if (event.data.type === "tokenClear") {
        dispatch(syncToken({}));
      }
    };
  }, [dispatch, token]);

  // watch isLoginAnimationVisible changes
  useEffect(() => {
    if (isLoginAnimationVisible) {
      setTimeout(() => {
        setIsRemovingAnimation(true);
      }, 2e3 - 300);
      setTimeout(() => {
        dispatch(setIsLoginAnimationVisible(false));
      }, 2e3); // 2s
    }
  }, [isLoginAnimationVisible, dispatch]);

  useEffect(() => {
    if (isRemovingAnimation) {
      setTimeout(() => {
        setIsRemovingAnimation(false);
      }, 1e3); // 1s
    }
  }, [isRemovingAnimation]);

  return (
    <KamaModuleContext.Provider
      value={{ primaryColor: "#0075ff", secondaryColor: "#0075ff" }}
    >
      {!statusLoading && (!token || !isIframe) ? (
        <RouterProvider
          router={token ? authorizedRouter : unauthorizedRouter}
        />
      ) : null}

      {isLoginAnimationVisible || isRemovingAnimation ? (
        <div
          className={classNames(styles.successLogin, {
            [styles.isRemoving]: isRemovingAnimation,
          })}
        >
          <div className={styles.animationContainer}>
            <DotLottiePlayer src={SuccessLoginJSON} loop={false} autoplay />
          </div>
        </div>
      ) : null}
    </KamaModuleContext.Provider>
  );
}

export default App;
